import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { mutate } from "swr";

import { useAuth } from "@/contexts/auth-context";
import { useCompanyApi } from "@/hooks/use-company-api";
import { isCompanyWorkspaceCacheKey } from "@/lib/context-cache-keys";
import type { Company } from "@/lib/generated/types.gen";

interface CompanyContextType {
  availableCompanies: Company[];
  currentCompany: Company | null;
  selectedCompanyId: number | null;
  companiesLoading: boolean;
  companiesError: Error | null;
  refreshCompanies: () => Promise<void>;
  switchCompany: (companyId: number) => Promise<void>;
  switchCompanyTemporarily: (companyId: number) => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | null>(null);

function getCurrentCompanyStorageKey(userId: string): string {
  return `currentCompanyId:${encodeURIComponent(userId)}`;
}

function parseCompanyID(rawCompanyID: string | null | undefined): number | null {
  if (rawCompanyID == null || rawCompanyID === "") {
    return null;
  }

  const parsedCompanyID = Number.parseInt(rawCompanyID, 10);
  if (Number.isNaN(parsedCompanyID)) {
    return null;
  }

  return parsedCompanyID;
}

export function selectInitialCompanyID(
  availableCompanies: Company[],
  defaultCompanyId?: number | null,
  storedCompanyId?: string | null,
): number | null {
  if (availableCompanies.length === 0) {
    return null;
  }

  if (defaultCompanyId != null) {
    const defaultCompany = availableCompanies.find((company) => company.id === defaultCompanyId);
    if (defaultCompany != null) {
      return defaultCompany.id;
    }
  }

  const parsedStoredCompanyID = parseCompanyID(storedCompanyId);
  if (parsedStoredCompanyID != null) {
    const storedCompany = availableCompanies.find(
      (company) => company.id === parsedStoredCompanyID,
    );
    if (storedCompany != null) {
      return storedCompany.id;
    }
  }

  return availableCompanies[0]?.id ?? null;
}

export function shouldPersistResolvedCompanyID(
  availableCompanies: Company[],
  resolvedCompanyId: number | null,
  defaultCompanyId?: number | null,
): boolean {
  if (resolvedCompanyId == null || availableCompanies.length === 0) {
    return false;
  }

  if (defaultCompanyId == null) {
    return true;
  }

  // Only repair a missing or invalid backend default. A valid default that differs from the
  // resolved company comes from a manual switch, which persists itself, or from another tab.
  return !availableCompanies.some((company) => company.id === defaultCompanyId);
}

interface CompanyProviderProps {
  children: ReactNode;
}

type PersistRequest = {
  companyId: number;
  initiatingUserId: string;
};

type PersistPromiseState = {
  companyId: number;
  initiatingUserId: string;
  promise: Promise<void>;
};

export function CompanyProvider({ children }: CompanyProviderProps) {
  const { userId, defaultCompanyId, isLoading: authLoading } = useAuth();
  const { useCompanies, updateDefaultCompany } = useCompanyApi();
  const {
    data: availableCompanies = [],
    error: companiesError,
    isLoading: companiesLoading,
    mutate: mutateCompanies,
  } = useCompanies(userId != null && authLoading === false);

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const autoPersistedCompanyIdRef = useRef<number | null>(null);
  const autoPersistInFlightCompanyIdRef = useRef<PersistRequest | null>(null);
  const queuedPersistCompanyIdRef = useRef<PersistRequest | null>(null);
  const persistDefaultCompanyPromiseRef = useRef<PersistPromiseState | null>(null);
  const temporaryCompanyIdRef = useRef<number | null>(null);
  const previousUserIdRef = useRef<string | null>(null);
  const selectionResetPendingRef = useRef(false);
  const selectedCompanyUserIdRef = useRef<string | null>(userId);
  const activeUserIdRef = useRef<string | null>(userId);
  activeUserIdRef.current = userId;

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (previousUserIdRef.current === userId) {
      return;
    }

    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId != null && previousUserId !== userId) {
      void mutate((key) => isCompanyWorkspaceCacheKey(key, previousUserId), undefined, false);
    }

    selectionResetPendingRef.current = true;
    setSelectedCompanyId(null);
    autoPersistedCompanyIdRef.current = null;
    autoPersistInFlightCompanyIdRef.current = null;
    queuedPersistCompanyIdRef.current = null;
    persistDefaultCompanyPromiseRef.current = null;
    temporaryCompanyIdRef.current = null;
    selectedCompanyUserIdRef.current = null;
  }, [authLoading, userId]);

  useEffect(() => {
    if (authLoading || companiesLoading || userId == null) {
      return;
    }

    if (availableCompanies.length === 0) {
      setSelectedCompanyId(null);
      autoPersistedCompanyIdRef.current = null;
      autoPersistInFlightCompanyIdRef.current = null;
      queuedPersistCompanyIdRef.current = null;
      persistDefaultCompanyPromiseRef.current = null;
      temporaryCompanyIdRef.current = null;
      selectionResetPendingRef.current = false;
      selectedCompanyUserIdRef.current = null;
      return;
    }

    if (selectedCompanyUserIdRef.current === userId && selectedCompanyId != null) {
      const selectedCompany = availableCompanies.find(
        (company) => company.id === selectedCompanyId,
      );
      if (selectedCompany != null) {
        return;
      }
    }

    temporaryCompanyIdRef.current = null;

    const storedCompanyID =
      typeof window !== "undefined"
        ? localStorage.getItem(getCurrentCompanyStorageKey(userId))
        : null;
    const initialCompanyID = selectInitialCompanyID(
      availableCompanies,
      defaultCompanyId,
      storedCompanyID,
    );
    selectedCompanyUserIdRef.current = userId;
    setSelectedCompanyId(initialCompanyID);

    if (initialCompanyID != null && typeof window !== "undefined") {
      localStorage.setItem(getCurrentCompanyStorageKey(userId), initialCompanyID.toString());
    }
  }, [
    authLoading,
    availableCompanies,
    companiesLoading,
    defaultCompanyId,
    selectedCompanyId,
    userId,
  ]);

  const isPersistRequestCurrent = useCallback((request: PersistRequest): boolean => {
    return activeUserIdRef.current === request.initiatingUserId;
  }, []);

  const persistDefaultCompany = useCallback(
    (companyId: number): Promise<void> => {
      const initiatingUserId = activeUserIdRef.current;
      if (initiatingUserId == null) {
        return Promise.reject(new Error("User must be authenticated to persist company selection"));
      }

      const request: PersistRequest = { companyId, initiatingUserId };
      const currentPersist = persistDefaultCompanyPromiseRef.current;

      if (autoPersistedCompanyIdRef.current === companyId) {
        return currentPersist?.promise ?? Promise.resolve();
      }

      if (
        (queuedPersistCompanyIdRef.current?.companyId === companyId &&
          queuedPersistCompanyIdRef.current.initiatingUserId === initiatingUserId) ||
        (autoPersistInFlightCompanyIdRef.current?.companyId === companyId &&
          autoPersistInFlightCompanyIdRef.current.initiatingUserId === initiatingUserId)
      ) {
        return currentPersist?.promise ?? Promise.resolve();
      }

      queuedPersistCompanyIdRef.current = request;

      const queuedWrite = (currentPersist?.promise ?? Promise.resolve())
        .catch(() => undefined)
        .then(async () => {
          if (!isPersistRequestCurrent(request)) {
            return;
          }

          if (
            queuedPersistCompanyIdRef.current?.companyId === companyId &&
            queuedPersistCompanyIdRef.current.initiatingUserId === initiatingUserId
          ) {
            queuedPersistCompanyIdRef.current = null;
          }

          autoPersistInFlightCompanyIdRef.current = request;
          await updateDefaultCompany(companyId, { initiatingUserId });

          if (!isPersistRequestCurrent(request)) {
            return;
          }

          autoPersistedCompanyIdRef.current = companyId;
        })
        .finally(() => {
          if (
            autoPersistInFlightCompanyIdRef.current?.companyId === companyId &&
            autoPersistInFlightCompanyIdRef.current.initiatingUserId === initiatingUserId
          ) {
            autoPersistInFlightCompanyIdRef.current = null;
          }
          if (
            queuedPersistCompanyIdRef.current?.companyId === companyId &&
            queuedPersistCompanyIdRef.current.initiatingUserId === initiatingUserId
          ) {
            queuedPersistCompanyIdRef.current = null;
          }
          if (
            persistDefaultCompanyPromiseRef.current?.companyId === companyId &&
            persistDefaultCompanyPromiseRef.current.initiatingUserId === initiatingUserId &&
            persistDefaultCompanyPromiseRef.current.promise === queuedWrite
          ) {
            persistDefaultCompanyPromiseRef.current = null;
          }
        });

      persistDefaultCompanyPromiseRef.current = {
        companyId,
        initiatingUserId,
        promise: queuedWrite,
      };
      return queuedWrite;
    },
    [isPersistRequestCurrent, updateDefaultCompany],
  );

  useEffect(() => {
    if (authLoading || companiesLoading || userId == null) {
      return;
    }

    if (selectionResetPendingRef.current) {
      return;
    }

    if (selectedCompanyUserIdRef.current !== userId) {
      return;
    }

    if (temporaryCompanyIdRef.current === selectedCompanyId) {
      return;
    }

    if (!shouldPersistResolvedCompanyID(availableCompanies, selectedCompanyId, defaultCompanyId)) {
      return;
    }

    const companyIdToPersist = selectedCompanyId;
    if (companyIdToPersist == null) {
      return;
    }

    if (autoPersistedCompanyIdRef.current === companyIdToPersist) {
      return;
    }

    void persistDefaultCompany(companyIdToPersist).catch((error: unknown) => {
      console.error("Failed to persist resolved company selection:", error);
    });
  }, [
    authLoading,
    availableCompanies,
    companiesLoading,
    defaultCompanyId,
    persistDefaultCompany,
    selectedCompanyId,
    userId,
  ]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (selectionResetPendingRef.current) {
      selectionResetPendingRef.current = false;
    }
  }, [authLoading, selectedCompanyId, userId]);

  const refreshCompanies = useCallback(async (): Promise<void> => {
    await mutateCompanies();
  }, [mutateCompanies]);

  const switchCompany = async (companyId: number): Promise<void> => {
    const selectingCurrentPersistentCompany =
      companyId === selectedCompanyId && temporaryCompanyIdRef.current !== companyId;
    if (selectingCurrentPersistentCompany) {
      return;
    }
    if (userId == null) {
      throw new Error("User must be authenticated to switch companies");
    }

    const nextCompany = availableCompanies.find((company) => company.id === companyId);
    if (nextCompany == null) {
      throw new Error("Selected company is not available");
    }

    const initiatingUserId = userId;
    await persistDefaultCompany(companyId);

    if (activeUserIdRef.current !== initiatingUserId) {
      return;
    }

    temporaryCompanyIdRef.current = null;
    autoPersistedCompanyIdRef.current = companyId;
    autoPersistInFlightCompanyIdRef.current = null;
    selectedCompanyUserIdRef.current = initiatingUserId;
    setSelectedCompanyId(companyId);

    if (typeof window !== "undefined") {
      localStorage.setItem(getCurrentCompanyStorageKey(initiatingUserId), companyId.toString());
    }
  };

  const switchCompanyTemporarily = async (companyId: number): Promise<void> => {
    if (companyId === selectedCompanyId && temporaryCompanyIdRef.current === companyId) {
      return;
    }

    const nextCompany = availableCompanies.find((company) => company.id === companyId);
    if (nextCompany == null) {
      throw new Error("Selected company is not available");
    }

    temporaryCompanyIdRef.current = companyId;
    selectedCompanyUserIdRef.current = activeUserIdRef.current;
    setSelectedCompanyId(companyId);
  };

  const currentCompany =
    availableCompanies.find((company) => company.id === selectedCompanyId) ?? null;

  return (
    <CompanyContext.Provider
      value={{
        availableCompanies,
        currentCompany,
        selectedCompanyId,
        companiesLoading,
        companiesError: companiesError ?? null,
        refreshCompanies,
        switchCompany,
        switchCompanyTemporarily,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany(): CompanyContextType {
  const context = useContext(CompanyContext);
  if (context == null) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
}
